import { supabase } from '../supabase';

export interface Ebook {
  id: number;
  title_ml: { it: string; bn: string; en: string };
  description_ml: { it: string; bn: string; en: string };
  pdf_url: string;
  cover_url?: string | null;
  author?: string | null;
  level: string;
  order_index: number;
}

// ─── Fallback demo data ───────────────────────────────────────────────────────

export const FALLBACK_EBOOKS: Ebook[] = [
  {
    id: 1,
    title_ml: { it: 'Italiano per Principianti', bn: 'শিক্ষার্থীদের জন্য ইতালীয়', en: 'Italian for Beginners' },
    description_ml: {
      it: 'Una guida completa per chi inizia a imparare l\'italiano.',
      bn: 'যারা ইতালীয় শেখা শুরু করছেন তাদের জন্য একটি সম্পূর্ণ গাইড।',
      en: 'A comprehensive guide for those who are starting to learn Italian.',
    },
    pdf_url: '',
    cover_url: null,
    author: 'Shikhun Italian',
    level: 'Beginner',
    order_index: 1,
  },
  {
    id: 2,
    title_ml: { it: 'Grammatica Italiana Essenziale', bn: 'মূল ইতালীয় ব্যাকরণ', en: 'Essential Italian Grammar' },
    description_ml: {
      it: 'Le regole grammaticali più importanti spiegate in modo semplice.',
      bn: 'সহজভাবে ব্যাখ্যা করা সবচেয়ে গুরুত্বপূর্ণ ব্যাকরণ নিয়ম।',
      en: 'The most important grammar rules explained simply.',
    },
    pdf_url: '',
    cover_url: null,
    author: 'Shikhun Italian',
    level: 'Intermediate',
    order_index: 2,
  },
];

// ─── API ──────────────────────────────────────────────────────────────────────

export async function fetchEbooks(): Promise<Ebook[]> {
  try {
    const { data, error } = await supabase
      .from('ebooks')
      .select('id, title_ml, description_ml, pdf_url, cover_url, author, level, order_index')
      .eq('is_published', true)
      .order('order_index', { ascending: true });
    if (error) throw error;
    return (data || []) as Ebook[];
  } catch {
    return FALLBACK_EBOOKS;
  }
}

// ─── Unlock helpers (localStorage + Supabase) ─────────────────────────────────

const UNLOCK_KEY = 'ebookUnlocks'; // { [ebookId]: expiresAtMs }

function getLocalUnlocks(): Record<string, number> {
  try {
    const raw = localStorage.getItem(UNLOCK_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

/** Returns true if the ebook is currently unlocked (not expired). */
export function isEbookUnlocked(ebookId: number): boolean {
  const unlocks = getLocalUnlocks();
  const expiresAt = unlocks[String(ebookId)];
  if (!expiresAt) return false;
  return Date.now() < expiresAt;
}

/** Returns how many ms until the unlock expires, or 0 if not unlocked / expired. */
export function ebookUnlockExpiresIn(ebookId: number): number {
  const unlocks = getLocalUnlocks();
  const expiresAt = unlocks[String(ebookId)];
  if (!expiresAt) return 0;
  return Math.max(0, expiresAt - Date.now());
}

/** Unlocks an ebook for 7 days, both locally and in Supabase (if authenticated). */
export async function unlockEbook(ebookId: number, userId?: string): Promise<void> {
  const expiresAt = Date.now() + 7 * 24 * 60 * 60 * 1000; // 7 days

  // Persist locally so it survives without a DB call
  const unlocks = getLocalUnlocks();
  unlocks[String(ebookId)] = expiresAt;
  localStorage.setItem(UNLOCK_KEY, JSON.stringify(unlocks));

  // Also persist in DB for cross-device sync when logged in
  if (userId && userId !== 'guest') {
    try {
      await supabase.from('ebook_unlocks').upsert(
        {
          user_id: userId,
          ebook_id: ebookId,
          unlocked_at: new Date().toISOString(),
          expires_at: new Date(expiresAt).toISOString(),
        },
        { onConflict: 'user_id,ebook_id' }
      );
    } catch {
      // Non-critical — local unlock is enough
    }
  }
}

/** On login, sync server unlocks into localStorage so they survive offline. */
export async function syncEbookUnlocksFromServer(userId: string): Promise<void> {
  if (!userId || userId === 'guest') return;
  try {
    const { data } = await supabase
      .from('ebook_unlocks')
      .select('ebook_id, expires_at')
      .eq('user_id', userId)
      .gt('expires_at', new Date().toISOString());

    if (!data) return;
    const unlocks = getLocalUnlocks();
    data.forEach((row: any) => {
      unlocks[String(row.ebook_id)] = new Date(row.expires_at).getTime();
    });
    localStorage.setItem(UNLOCK_KEY, JSON.stringify(unlocks));
  } catch {
    // Non-critical
  }
}
